import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, ArrowRight, Loader2 } from 'lucide-react';

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        if (!sessionId) { setStatus('error'); return; }
        const check = async () => {
            try {
                const { data: res } = await API.get(`/payments/checkout/status/${sessionId}`);
                setData(res);
                setStatus(res.payment_status === 'paid' ? 'success' : res.payment_status === 'unpaid' ? 'pending' : 'error');
            } catch (err) { setStatus('error'); }
        };
        check();
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="payment-success-page">
            <Navbar variant="light" />
            <div className="max-w-lg mx-auto px-4 pt-32 text-center">
                {status === 'loading' && (
                    <div className="animate-fade-in-up">
                        <Loader2 className="w-12 h-12 text-emerald-600 mx-auto mb-4 animate-spin" />
                        <h1 className="text-xl font-semibold text-zinc-900 font-['Outfit']">Verifying Payment...</h1>
                    </div>
                )}
                {status === 'success' && (
                    <div className="animate-fade-in-up">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-emerald-700" />
                        </div>
                        <h1 className="text-2xl font-semibold text-zinc-900 font-['Outfit'] mb-2">Payment Successful!</h1>
                        <p className="text-sm text-zinc-500 mb-1">Amount: ${(data?.amount_total / 100)?.toFixed(2)}</p>
                        <p className="text-sm text-zinc-500 mb-6">Your credit repair program payment has been processed.</p>
                        <Button onClick={() => navigate('/credit-repair')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-8" data-testid="go-to-dashboard-btn">
                            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                )}
                {status === 'pending' && (
                    <div className="animate-fade-in-up">
                        <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                        <h1 className="text-xl font-semibold text-zinc-900 font-['Outfit'] mb-2">Payment Processing</h1>
                        <p className="text-sm text-zinc-500 mb-6">Your payment is being processed. This may take a moment.</p>
                        <Button onClick={() => navigate('/credit-repair')} variant="outline" className="border-zinc-200">
                            Return to Dashboard
                        </Button>
                    </div>
                )}
                {status === 'error' && (
                    <div className="animate-fade-in-up">
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="text-xl font-semibold text-zinc-900 font-['Outfit'] mb-2">Payment Issue</h1>
                        <p className="text-sm text-zinc-500 mb-6">There was an issue verifying your payment. Please contact support.</p>
                        <Button onClick={() => navigate('/credit-repair')} variant="outline" className="border-zinc-200">
                            Return to Dashboard
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
